import { useMutation } from '@apollo/client';
import { useState } from 'react';
import {
    IncomeSharingMode,
    UpdatePartnershipDocument,
    UpdateUserProfileDocument,
} from '../graphql/generated';
import { isUndefined } from '../util/types';
import { useCurrentUser } from './user';

export const INCOME_SHARING_MODE_OPTIONS: {
    label: string;
    value: IncomeSharingMode;
}[] = [
    {
        label: 'Combined',
        value: 'COMBINED',
    },
    {
        label: 'Separate',
        value: 'SEPARATE',
    },
];

export interface NetIncomeFormProps {
    monthlyNetIncome: number | null | undefined;
    partnerMonthlyNetIncome: number | null | undefined;
    incomeSharingMode: IncomeSharingMode;
    setMonthlyNetIncome: (value: number) => void;
    setPartnerMonthlyNetIncome: (value: number) => void;
    setIncomeSharingMode: (value: IncomeSharingMode) => void;
    submit: () => Promise<void>;
    isSubmitting: boolean;
}

export const useBaseNetIncomeFormProps = ({
    onComplete,
    onError,
}: {
    onComplete: VoidFunction;
    onError: (error: unknown) => void;
}): NetIncomeFormProps => {
    const currentUser = useCurrentUser();
    const { userInfo, partnerInfo, activePartnershipId } = currentUser;

    const [updateUserProfile] = useMutation(UpdateUserProfileDocument);
    const [updatePartnership] = useMutation(UpdatePartnershipDocument);
    const [monthlyNetIncome, setMonthlyNetIncome] = useState(
        userInfo.monthlyNetIncome
    );
    const [partnerMonthlyNetIncome, setPartnerMonthlyNetIncome] = useState(
        partnerInfo?.monthlyNetIncome
    );
    const [incomeSharingMode, setIncomeSharingMode] =
        useState<IncomeSharingMode>(
            partnerInfo?.incomeSharingMode || 'COMBINED'
        );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submit = async () => {
        if (isUndefined(monthlyNetIncome)) return;

        setIsSubmitting(true);

        try {
            await updateUserProfile({
                variables: {
                    patch: {
                        monthlyNetIncome,
                    },
                },
            });

            if (partnerMonthlyNetIncome && activePartnershipId) {
                await updatePartnership({
                    variables: {
                        partnershipId: activePartnershipId,
                        partnerMonthlyNetIncome,
                        incomeSharingMode,
                    },
                });
            }

            onComplete();
        } catch (e) {
            onError(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        isSubmitting,
        submit,
        monthlyNetIncome,
        setMonthlyNetIncome,
        partnerMonthlyNetIncome,
        setPartnerMonthlyNetIncome,
        incomeSharingMode,
        setIncomeSharingMode,
    };
};
